import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useSignIn, useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import posthog from "posthog-js";

import {
  onboardingApi,
  VISIBLE_STEPS,
  type OnboardingState,
  type OnboardingStep,
  type PipelineStage,
} from "@/api/onboarding-api";
import { workspaceApi } from "@/api/workspace-api";
import { aiApi } from "@/api/ai-api";
import { createDeal, editDealInfo } from "@/api/deals-api";
import { filesApi } from "@/api/files-api";
import { teamApi } from "@/api/team-api";
import { getErrorMessage } from "@/lib/api-error";
import { useWorkspaceStore } from "@/store/workspace-store";
import { ONBOARDING_STATE_KEY } from "@/components/auth/onboarding-gate";

import { AssistantBubble, TypingIndicator, UserBubble } from "./chat-bubble";
import { ProgressRail } from "./progress-rail";
import { Backdrop, ProvisioningSplash } from "./chrome";
import { WorkspaceStep } from "./steps/workspace-step";
import { AccountStep, VerifyCodeStep, type AccountDraft } from "./steps/account-step";
import {
  ThesisInput,
  ThesisReview,
  draftFromWorkspace,
  summariseThesis,
  type ThesisDraft,
} from "./steps/thesis-step";
import {
  PipelineStep,
  mergeParsed,
  renumber,
  stripIds,
  withIds,
  type EditableStage,
} from "./steps/pipeline-step";
import { DealStep, type DealDraft } from "./steps/deal-step";
import { InviteStep } from "./steps/invite-step";
import { FinishStep } from "./steps/finish-step";

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  text: string;
  /** Replayed from the saved transcript — render immediately, don't re-type. */
  instant: boolean;
}

/** Which interactive widget is currently offered, if any. */
type Widget =
  | "account"
  | "verify-code"
  | "workspace"
  | "thesis-input"
  | "thesis-review"
  | "pipeline"
  | "deal"
  | "invite"
  | "finish"
  | null;

let messageCounter = 0;
const nextMessageId = () => `msg-${++messageCounter}`;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** How close to the bottom (px) still counts as "following" the conversation. */
const FOLLOW_THRESHOLD_PX = 120;

/** Space kept above a new widget when scrolling to it, so its heading stays visible. */
const WIDGET_TOP_MARGIN = 24;

/** Default role for onboarding invites — editable later in Team settings. */
const DEFAULT_INVITE_ROLE = "Partner";

/** How a step's opening line greets the user. */
type Opening = "welcome" | "signed-up" | "none";

/** Clerk errors carry their message in `errors[0].longMessage`. */
function clerkErrorMessage(error: unknown, fallback: string): string {
  const clerkMessage = (error as { errors?: Array<{ longMessage?: string }> })?.errors?.[0]
    ?.longMessage;
  return clerkMessage || getErrorMessage(error, fallback);
}

/** Only same-origin paths — `redirect_url` comes from the query string. */
function safeReturnPath(value: string | null): string | null {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : null;
}

/** Splits an email local part into a plausible first/last name for the invite API. */
function nameFromEmail(email: string): { first: string; last: string } {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return {
    first: cap(parts[0] ?? "New"),
    last: cap(parts[1] ?? "Member"),
  };
}

export function OnboardingChat() {
  const navigate = useNavigate();
  const { user: clerkUser, isLoaded: isClerkLoaded, isSignedIn } = useUser();
  const { signIn, setActive } = useSignIn();
  const [searchParams] = useSearchParams();
  const shouldReduce = useReducedMotion();
  const { fetchWorkspaces, setActiveWorkspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();

  const [state, setState] = useState<OnboardingState | null>(null);
  const [phase, setPhase] = useState<"loading" | "provisioning" | "chatting">("loading");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [thinking, setThinking] = useState<string | null>(null);
  const [widget, setWidget] = useState<Widget>(null);
  const [busy, setBusy] = useState(false);

  // Step-local working state.
  const [thesisDraft, setThesisDraft] = useState<ThesisDraft | null>(null);
  const [thesisManual, setThesisManual] = useState(false);
  const [stages, setStages] = useState<EditableStage[]>([]);
  const [isParsingStages, setIsParsingStages] = useState(false);
  const [dealProgress, setDealProgress] = useState(0);
  const [dealProgressLabel, setDealProgressLabel] = useState("");
  const [finishedDeal, setFinishedDeal] = useState<{ id: string; name: string } | null>(
    null
  );
  /** What the sign-up turn collected — kept so "use a different email" can prefill. */
  const [accountDraft, setAccountDraft] = useState<AccountDraft | null>(null);

  /** Resolvers for `say()`, keyed by message id and cleared on first call. */
  const resolversRef = useRef(new Map<string, () => void>());
  /** Turns accumulated during the current step, persisted when it completes. */
  const turnsRef = useRef<
    Array<{ role: "assistant" | "user"; content?: string; payload?: Record<string, unknown> }>
  >([]);
  const bootstrappedRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  /** True while the user is at (or near) the bottom — new content keeps them there. */
  const followLatestRef = useRef(true);
  /** Set just before we scroll, so our own scroll event isn't read as the user's. */
  const isProgrammaticScrollRef = useRef(false);

  const workspaceId = state?.workspace?._id ?? state?.onboarding.workspace ?? null;
  /**
   * Read through a ref because the scripts run inside async chains started
   * before the user signed in — a closed-over value would still say "there".
   */
  const firstNameRef = useRef<string | null>(null);
  firstNameRef.current =
    state?.user.firstName || clerkUser?.firstName || accountDraft?.firstName || null;

  // ── Conversation primitives ──────────────────────────────────

  /** Pushes an assistant line and resolves once it has finished typing. */
  const say = useCallback((text: string) => {
    turnsRef.current.push({ role: "assistant", content: text });
    return new Promise<void>((resolve) => {
      const id = nextMessageId();
      resolversRef.current.set(id, resolve);
      setMessages((prev) => [...prev, { id, role: "assistant", text, instant: false }]);
    });
  }, []);

  const onBubbleTyped = useCallback((id: string) => {
    const resolve = resolversRef.current.get(id);
    if (resolve) {
      resolversRef.current.delete(id);
      resolve();
    }
  }, []);

  const pushUser = useCallback((text: string, payload?: Record<string, unknown>) => {
    turnsRef.current.push({ role: "user", content: text, payload });
    setMessages((prev) => [
      ...prev,
      { id: nextMessageId(), role: "user", text, instant: false },
    ]);
  }, []);

  // ── Step scripts ─────────────────────────────────────────────

  const scriptFor = useCallback(
    (step: OnboardingStep, opening: Opening): string[] => {
      const name = firstNameRef.current || "there";
      switch (step) {
        case "deal":
          return [
            opening === "welcome"
              ? `Hey ${name} — welcome to DiligenceGPT. Let's start with a real deal.`
              : opening === "signed-up"
                ? `You're in, ${name}. Let's start with a real deal.`
                : "Let's get a real deal in here.",
            "Type a company name and I'll find their website and LinkedIn. The analysis takes a few minutes, so it runs in the background while we set up the rest — or skip this and add one later.",
          ];
        case "workspace":
          return [
            "Now your workspace — this is where your deals live.",
            "What should we call it, and what kind of investor are you?",
          ];
        case "thesis":
          return [
            "Now the part that does the heavy lifting: what do you invest in?",
            "Describe it however you like, or paste a link to your site — I'll turn it into screening criteria and score every deal against it.",
          ];
        case "pipeline":
          return [
            "This is the pipeline your deals will move through.",
            "Drag to reorder, rename anything inline, or just tell me what to change.",
          ];
        case "invite":
          return ["Anyone else joining you? Entirely optional — you can skip this."];
        default:
          return [];
      }
    },
    []
  );

  const widgetFor = (step: OnboardingStep): Widget => {
    switch (step) {
      case "workspace":
        return "workspace";
      case "thesis":
        return "thesis-input";
      case "pipeline":
        return "pipeline";
      case "deal":
        return "deal";
      case "invite":
        return "invite";
      default:
        return "finish";
    }
  };

  /** Speaks a step's opening lines, then reveals its widget. */
  const runStep = useCallback(
    async (step: OnboardingStep, opening: Opening = "none") => {
      turnsRef.current = [];
      setWidget(null);

      if (step === "done") {
        setWidget("finish");
        return;
      }

      for (const line of scriptFor(step, opening)) {
        await say(line);
        await wait(shouldReduce ? 0 : 200);
      }

      setWidget(widgetFor(step));
    },
    [say, scriptFor, shouldReduce]
  );

  /**
   * Persists the finished step and moves on. The server decides what comes
   * next, so a stale client can't skip a step it shouldn't.
   */
  const advance = useCallback(
    async (step: Exclude<OnboardingStep, "done">, extra?: { first_deal_company?: string }) => {
      const { onboarding } = await onboardingApi.completeStep({
        step,
        turns: turnsRef.current,
        ...extra,
      });

      setState((prev) => (prev ? { ...prev, onboarding } : prev));
      posthog.capture("onboarding_step_completed", { step });

      await wait(shouldReduce ? 0 : 260);
      await runStep(onboarding.current_step);
    },
    [runStep, shouldReduce]
  );

  // ── Bootstrap ────────────────────────────────────────────────

  /**
   * Loads (and if needed provisions) the signed-in user's onboarding, then
   * resumes it. `continuing` means they just signed up in this chat: their
   * sign-up turns stay on screen and provisioning shows inline rather than
   * replacing the conversation with a splash.
   */
  const bootstrap = useCallback(
    async ({ continuing }: { continuing: boolean }) => {
      try {
        let current = await onboardingApi.getState();

        // An existing account that signed in from /get-started has nothing to
        // set up — send it where it would have gone from /sign-in.
        const alreadyDone =
          current.onboarding.status === "completed" || current.onboarding.status === "skipped";
        if (continuing && alreadyDone) {
          await fetchWorkspaces().catch(() => undefined);
          const existingWorkspace = current.onboarding.workspace;
          if (existingWorkspace) setActiveWorkspaceId(existingWorkspace);
          navigate(
            safeReturnPath(searchParams.get("redirect_url")) ??
              (existingWorkspace ? `/${existingWorkspace}/dashboard` : "/workspaces"),
            { replace: true }
          );
          return;
        }

        if (current.needs_provisioning) {
          if (continuing) setThinking("Setting up your workspace…");
          else setPhase("provisioning");
          // A beat on the splash — the request usually returns faster than the
          // copy can be read, and a flash of "setting up" reads as a glitch.
          const [provisioned] = await Promise.all([
            onboardingApi.provision(),
            wait(shouldReduce || continuing ? 0 : 1100),
          ]);
          current = {
            ...current,
            needs_provisioning: false,
            company: provisioned.company,
            workspace: provisioned.workspace,
            onboarding: provisioned.onboarding,
          };
          // The workspace store is populated at app start, before this
          // workspace existed — refresh it so navigation out of here works.
          await fetchWorkspaces().catch(() => undefined);
          setThinking(null);
        }

        firstNameRef.current = current.user.firstName || firstNameRef.current;
        setState(current);
        setPhase("chatting");

        // Replay whatever the user already answered, instantly.
        const replayed = current.onboarding.transcript
          .filter((t) => t.content)
          .map((t) => ({
            id: nextMessageId(),
            role: t.role,
            text: t.content as string,
            instant: true,
          }));
        setMessages((prev) => (continuing ? [...prev, ...replayed] : replayed));

        if (current.workspace) {
          setStages(withIds(normaliseStages(current.workspace.statuses)));
          setThesisDraft(draftFromWorkspace(current.workspace));
        }

        await wait(shouldReduce ? 0 : 350);
        const opening: Opening = continuing
          ? "signed-up"
          : replayed.length === 0
            ? "welcome"
            : "none";
        await runStep(current.onboarding.current_step, opening);
      } catch (error) {
        console.error("Onboarding bootstrap failed", error);
        setThinking(null);
        toast.error(getErrorMessage(error, "Couldn't start onboarding. Please refresh."));
        setPhase("chatting");
      }
    },
    [fetchWorkspaces, navigate, runStep, searchParams, setActiveWorkspaceId, shouldReduce]
  );

  /** Signed-out visitors (on /get-started) start by creating their account. */
  const startSignup = useCallback(async () => {
    setPhase("chatting");
    await wait(shouldReduce ? 0 : 350);
    await say("Hey — welcome to DiligenceGPT. I'll have you set up in a few minutes.");
    await wait(shouldReduce ? 0 : 200);
    await say("First, who are you?");
    setWidget("account");
  }, [say, shouldReduce]);

  useEffect(() => {
    if (!isClerkLoaded || bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    void (isSignedIn ? bootstrap({ continuing: false }) : startSignup());
  }, [bootstrap, isClerkLoaded, isSignedIn, startSignup]);

  // ── Scrolling ────────────────────────────────────────────────
  // The chat scrolls inside its own container (the app shell locks the body).
  // We follow the conversation only while the user is at the bottom, so
  // scrolling up to re-read is never fought.

  /** Moves to the newest content without pushing the active widget's top off screen. */
  const scrollToLatest = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let target = container.scrollHeight - container.clientHeight;
    const widgetEl = widgetRef.current;
    if (widgetEl && widgetEl.offsetHeight > 0) {
      const widgetTop =
        widgetEl.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      target = Math.min(target, widgetTop - WIDGET_TOP_MARGIN);
    }

    // Never scroll the user backwards on their behalf.
    if (target <= container.scrollTop) return;
    isProgrammaticScrollRef.current = true;
    container.scrollTo({ top: target });
  }, []);

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    followLatestRef.current = distanceFromBottom < FOLLOW_THRESHOLD_PX;
  };

  // A new step always brings the user to it — they just acted.
  useEffect(() => {
    followLatestRef.current = true;
  }, [widget]);

  // Content grows as bubbles type and widgets animate in; follow it if we're following.
  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(() => {
      if (followLatestRef.current) scrollToLatest();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [phase, scrollToLatest]);

  // ── Step handlers ────────────────────────────────────────────

  /** Emails a sign-in code — the same path /sign-in uses. */
  const sendCode = async (email: string) => {
    if (!signIn) throw new Error("Sign-in isn't ready yet — try again in a moment.");
    const { supportedFirstFactors } = await signIn.create({ identifier: email });
    const factor = supportedFirstFactors?.find((f) => f.strategy === "email_code");
    if (!factor || !("emailAddressId" in factor)) {
      throw new Error("Email code sign-in isn't available for this account.");
    }
    await signIn.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: factor.emailAddressId,
    });
  };

  const handleAccount = async (draft: AccountDraft) => {
    setBusy(true);
    setWidget(null);
    setAccountDraft(draft);
    pushUser(`${draft.firstName} ${draft.lastName} · ${draft.email}`);

    try {
      const result = await onboardingApi.createAccount({
        first_name: draft.firstName,
        last_name: draft.lastName,
        email: draft.email,
      });
      await sendCode(result.email);
      setAccountDraft({ ...draft, email: result.email });
      posthog.capture("onboarding_account_submitted", { existing: result.status === "existing_user" });

      await wait(shouldReduce ? 0 : 200);
      await say(
        result.status === "existing_user"
          ? `${result.email} already has an account — I've sent a sign-in code so you can pick up where you left off.`
          : `Thanks, ${draft.firstName}. I've sent a 6-digit code to ${result.email} — enter it below.`
      );
      setWidget("verify-code");
    } catch (error) {
      console.error("Onboarding signup failed", error);
      toast.error(clerkErrorMessage(error, "Couldn't create your account — try again."));
      setWidget("account");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (!signIn || !setActive) return;
    setBusy(true);

    try {
      const result = await signIn.attemptFirstFactor({ strategy: "email_code", code });
      if (result.status !== "complete" || !result.createdSessionId) {
        throw new Error("Verification didn't complete — try the code again.");
      }
      await setActive({ session: result.createdSessionId });

      setWidget(null);
      pushUser("Verified");
      await bootstrap({ continuing: true });
    } catch (error) {
      console.error("Onboarding code verification failed", error);
      toast.error(clerkErrorMessage(error, "That code didn't work — try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleResendCode = async () => {
    if (!accountDraft) return;
    try {
      await sendCode(accountDraft.email);
      toast.success(`New code sent to ${accountDraft.email}`);
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Couldn't resend the code."));
    }
  };

  const handleChangeEmail = async () => {
    setWidget(null);
    pushUser("Use a different email");
    await wait(shouldReduce ? 0 : 180);
    await say("Sure — what's the right one?");
    setWidget("account");
  };

  const handleWorkspace = async ({ name, type }: { name: string; type: string }) => {
    if (!workspaceId) return;
    setBusy(true);

    try {
      await workspaceApi.updateWorkspace(workspaceId, {
        opportunity_name: name,
        opportunity_type: type,
      });

      setWidget(null);
      pushUser(`${name} · ${type}`, { name, type });
      setState((prev) =>
        prev && prev.workspace
          ? {
              ...prev,
              workspace: { ...prev.workspace, opportunity_name: name, opportunity_type: type },
            }
          : prev
      );
      await fetchWorkspaces().catch(() => undefined);

      await wait(shouldReduce ? 0 : 200);
      await say(`${name} it is.`);
      await advance("workspace");
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save that — try again."));
      setWidget("workspace");
    } finally {
      setBusy(false);
    }
  };

  const handleThesisAnalyze = async (context: string) => {
    if (!workspaceId) return;
    setBusy(true);
    setWidget(null);
    pushUser(context.length > 240 ? `${context.slice(0, 240)}…` : context);
    setThinking("Reading that and pulling out your criteria…");

    try {
      await workspaceApi.generateClientProfile(workspaceId, context);
      const refreshed = await workspaceApi.getWorkspace(workspaceId);
      const draft = draftFromWorkspace(refreshed.result ?? {});

      setThesisDraft(draft);
      setThinking(null);

      const found =
        draft.target_stage_companies.length +
        draft.target_gics_sub_industries.length +
        draft.target_model_companies.length +
        draft.geographical_location.length;

      await say(
        found > 0
          ? "Here's what I pulled out. Take anything off that doesn't fit."
          : "I couldn't pin much down from that — let's set the criteria directly."
      );
      setThesisManual(found === 0);
      setWidget("thesis-review");
    } catch (error) {
      setThinking(null);
      console.error("Thesis analysis failed", error);
      await say(
        "That didn't go through on my end — let's just pick the criteria directly instead."
      );
      setThesisManual(true);
      setWidget("thesis-review");
    } finally {
      setBusy(false);
    }
  };

  const handleThesisManual = async () => {
    setWidget(null);
    pushUser("I'll pick from a list");
    await wait(shouldReduce ? 0 : 180);
    await say("Sure — set whatever fits.");
    setThesisManual(true);
    setWidget("thesis-review");
  };

  const handleThesisConfirm = async () => {
    if (!workspaceId || !thesisDraft) return;
    setBusy(true);

    try {
      // `target_gics_industries` is the 8-character prefix of each sub-industry
      // code; the deal-fit scorer reads both levels.
      const derivedIndustries = [
        ...new Set(thesisDraft.target_gics_sub_industries.map((c) => c.substring(0, 8))),
      ];

      await workspaceApi.updateWorkspace(workspaceId, {
        target_gics_industries: derivedIndustries,
        target_gics_sub_industries: thesisDraft.target_gics_sub_industries,
        target_stage_companies: thesisDraft.target_stage_companies,
        target_model_companies: thesisDraft.target_model_companies,
        target_revenue_models: thesisDraft.target_revenue_models,
        geographical_location: thesisDraft.geographical_location,
        min_cheque_size: thesisDraft.min_cheque_size,
        max_cheque_size: thesisDraft.max_cheque_size,
        currency: thesisDraft.currency,
        additional_info: thesisDraft.additional_info,
      });

      // The first deal is added before the thesis exists, so its fit was
      // scored against nothing — re-run it now there's something to score
      // against. Best-effort: a failure only leaves the first score stale.
      const firstDeal = state?.onboarding.first_deal_company;
      if (firstDeal) {
        aiApi
          .getDealFit({ companyId: firstDeal, opportunityId: workspaceId, applicationObject: {} })
          .catch((error) => console.error("Re-scoring the first deal failed", error));
      }

      setWidget(null);
      pushUser(summariseThesis(thesisDraft));
      await wait(shouldReduce ? 0 : 200);
      await say("Locked in. Every deal you add gets scored against that.");
      await advance("thesis");
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save your thesis — try again."));
      setWidget("thesis-review");
    } finally {
      setBusy(false);
    }
  };

  const handleStageInstruction = async (instruction: string) => {
    pushUser(instruction);
    setIsParsingStages(true);

    try {
      const result = await onboardingApi.parseStages(instruction, stripIds(stages));
      if (result.understood) {
        setStages((prev) => mergeParsed(prev, result.stages));
      }
      await say(result.message);
    } catch (error) {
      console.error("Stage parsing failed", error);
      await say("That didn't go through — you can edit the stages directly above.");
    } finally {
      setIsParsingStages(false);
    }
  };

  const handlePipelineConfirm = async () => {
    if (!workspaceId) return;
    setBusy(true);

    try {
      await workspaceApi.updateWorkspace(workspaceId, {
        statuses: stripIds(stages),
      });

      setWidget(null);
      pushUser(stages.map((s) => s.title).join(" → "), { stages: stripIds(stages) });
      await fetchWorkspaces().catch(() => undefined);

      await wait(shouldReduce ? 0 : 200);
      await say("That's your board.");
      await advance("pipeline");
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save the pipeline — try again."));
      setWidget("pipeline");
    } finally {
      setBusy(false);
    }
  };

  const handleDeal = async (draft: DealDraft) => {
    if (!workspaceId || !clerkUser?.id) return;

    setBusy(true);
    setDealProgress(6);
    setDealProgressLabel(`Creating ${draft.companyName}…`);

    try {
      const created = await createDeal.createCompanyPitchbox({
        userId: clerkUser.id,
        workspaceId,
        companyName: draft.companyName,
        companyWebsite: draft.website,
      });

      // Any response without a company id is a failure whatever the status
      // says — reading `.result._id` blind would surface a raw TypeError.
      const companyId = created?.result?._id;
      if (!companyId) {
        throw new Error(created?.message || "Could not create that company.");
      }

      setDealProgress(30);

      // Files are uploaded before the application is created — the report
      // sequence is seeded from the company's files at creation time.
      if (draft.file) {
        setDealProgressLabel("Uploading the deck…");
        try {
          await filesApi.uploadFile(
            {
              file: draft.file,
              file_name: draft.file.name,
              file_type: "company pitchdeck",
              object_ref: "company",
              object_ref_value: companyId,
            },
            companyId,
            workspaceId
          );
        } catch (uploadError) {
          // Non-fatal: the deal is worth more than the attachment, and they
          // can re-upload from the data room.
          console.error("Onboarding deck upload failed", uploadError);
          toast.error("The deck didn't upload — you can add it from the deal's Data Room.");
        }
      }

      setDealProgress(60);
      setDealProgressLabel("Adding it to your pipeline…");
      await createDeal.createOpportunityApplication(
        companyId,
        workspaceId,
        stages[0]?.title || "New",
        "Manual"
      );

      setDealProgress(85);
      if (draft.linkedIn) {
        try {
          await editDealInfo.editDealInfo({
            workspaceId,
            companyId,
            fieldName: "linkedIn",
            fieldValue: draft.linkedIn,
          });
        } catch (linkedInError) {
          console.error("Onboarding LinkedIn save failed", linkedInError);
        }
      }

      setDealProgress(100);
      setDealProgressLabel("Done.");
      setFinishedDeal({ id: companyId, name: draft.companyName });

      setWidget(null);
      pushUser(draft.companyName, { companyId, website: draft.website });
      await wait(shouldReduce ? 0 : 250);
      await say(
        `${draft.companyName} is in — the analysis has started and keeps running while we set up the rest.`
      );
      await advance("deal", { first_deal_company: companyId });
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't add that deal — try again."));
      setWidget("deal");
    } finally {
      setBusy(false);
      setDealProgress(0);
      setDealProgressLabel("");
    }
  };

  const handleSkipDeal = async () => {
    setWidget(null);
    pushUser("I'll add one later");
    await wait(shouldReduce ? 0 : 180);
    await say("No problem — you can add deals from your pipeline any time.");
    await advance("deal");
  };

  const handleInvites = async (emails: string[]) => {
    if (!workspaceId) return;
    setBusy(true);

    try {
      const results = await Promise.allSettled(
        emails.map(async (email) => {
          const { first, last } = nameFromEmail(email);
          const judge = await teamApi.createJudge({
            first_name: first,
            last_name: last,
            email,
            sub_vertical: DEFAULT_INVITE_ROLE,
          });

          if (judge.status === "error" || !judge.data) {
            throw new Error(judge.message || `Could not invite ${email}`);
          }

          await teamApi.createAffiliation({
            user: judge.data._id,
            opportunity: workspaceId,
            status: "approved",
            role: DEFAULT_INVITE_ROLE,
          });
        })
      );

      const sent = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - sent;

      setWidget(null);
      pushUser(emails.join(", "), { emails });
      await wait(shouldReduce ? 0 : 200);

      if (sent > 0) {
        await say(
          failed > 0
            ? `Invited ${sent} of ${results.length} — you can add the rest from Team settings.`
            : `Invited ${sent === 1 ? "them" : `all ${sent}`}.`
        );
      } else {
        await say("None of those went through — you can add them from Team settings later.");
      }

      await advance("invite");
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't send those invites."));
      setWidget("invite");
    } finally {
      setBusy(false);
    }
  };

  const handleSkipInvites = async () => {
    setWidget(null);
    pushUser("I'll do this later");
    await wait(shouldReduce ? 0 : 180);
    await say("No problem.");
    await advance("invite");
  };

  /** Marks onboarding finished, then leaves. */
  const leave = async (destination: string) => {
    let finished: OnboardingState["onboarding"] | null = null;
    try {
      finished = (await onboardingApi.finish(false)).onboarding;
      posthog.capture("onboarding_completed");
    } catch (error) {
      // Not worth blocking the exit — a user who got this far has completed
      // the required steps, and the server re-checks on the next fresh load.
      console.error("Failed to mark onboarding complete", error);
    }

    // The gate caches onboarding state; without this it still reads
    // "in_progress" and bounces the user straight back here.
    queryClient.setQueryData<OnboardingState>(ONBOARDING_STATE_KEY, (prev) =>
      prev
        ? {
            ...prev,
            onboarding: finished ?? {
              ...prev.onboarding,
              status: "completed",
              current_step: "done",
            },
          }
        : prev
    );

    await fetchWorkspaces().catch(() => undefined);
    if (workspaceId) setActiveWorkspaceId(workspaceId);
    navigate(destination, { replace: true });
  };

  // Speak the closing line once the flow reaches its terminal step.
  useEffect(() => {
    if (widget !== "finish" || messages.some((m) => m.text.startsWith("That's everything"))) {
      return;
    }
    void say("That's everything — you're set up.");
  }, [widget, messages, say]);

  // ── Render ───────────────────────────────────────────────────

  if (phase === "loading") {
    return <ProvisioningSplash label="Getting things ready…" />;
  }

  if (phase === "provisioning") {
    return <ProvisioningSplash label="Building your workspace…" />;
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <Backdrop />

      <header className="relative z-20 shrink-0 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          {state ? (
            <>
              <ProgressRail
                currentStep={state.onboarding.current_step}
                completedSteps={state.onboarding.completed_steps}
              />
              <StepCounter step={state.onboarding.current_step} />
            </>
          ) : (
            <>
              <span className="text-sm font-medium text-foreground">Create your account</span>
              <span className="text-xs text-muted-foreground">
                Already have one?{" "}
                <Link
                  to={`/sign-in${searchParams.toString() ? `?${searchParams.toString()}` : ""}`}
                  className="font-medium text-primary hover:underline"
                >
                  Sign in
                </Link>
              </span>
            </>
          )}
        </div>
      </header>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="relative z-10 flex flex-1 flex-col overflow-y-auto overscroll-contain"
      >
      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-8 sm:px-6">
        <div ref={scrollContentRef} className="space-y-5">
          <AnimatePresence initial={false} mode="popLayout">
            {messages.map((message) =>
              message.role === "assistant" ? (
                <AssistantBubble
                  key={message.id}
                  text={message.text}
                  instant={message.instant}
                  onDone={() => onBubbleTyped(message.id)}
                />
              ) : (
                <UserBubble key={message.id} text={message.text} />
              )
            )}
          </AnimatePresence>

          <AnimatePresence>
            {thinking && <TypingIndicator key="thinking" label={thinking} />}
          </AnimatePresence>

          <div ref={widgetRef}>
          <AnimatePresence mode="wait">
            {widget === "account" && (
              <AccountStep
                key={`account-${accountDraft?.email ?? "new"}`}
                defaultDraft={accountDraft ?? undefined}
                onSubmit={handleAccount}
                isSubmitting={busy}
              />
            )}

            {widget === "verify-code" && accountDraft && (
              <VerifyCodeStep
                key="verify-code"
                email={accountDraft.email}
                onSubmit={handleVerifyCode}
                onResend={handleResendCode}
                onChangeEmail={handleChangeEmail}
                isSubmitting={busy}
              />
            )}

            {widget === "workspace" && state && (
              <WorkspaceStep
                key="workspace"
                defaultName={
                  state.workspace?.opportunity_name ||
                  (state.company ? `${state.company.companyName} Pipeline` : "")
                }
                defaultType={state.workspace?.opportunity_type}
                onSubmit={handleWorkspace}
                isSubmitting={busy}
              />
            )}

            {widget === "thesis-input" && (
              <ThesisInput
                key="thesis-input"
                onAnalyze={handleThesisAnalyze}
                onManual={handleThesisManual}
                isAnalyzing={busy}
              />
            )}

            {widget === "thesis-review" && thesisDraft && (
              <ThesisReview
                key="thesis-review"
                draft={thesisDraft}
                onChange={setThesisDraft}
                onConfirm={handleThesisConfirm}
                isSubmitting={busy}
                startExpanded={thesisManual}
              />
            )}

            {widget === "pipeline" && (
              <PipelineStep
                key="pipeline"
                stages={stages}
                onChange={setStages}
                onInstruction={handleStageInstruction}
                onConfirm={handlePipelineConfirm}
                isSubmitting={busy}
                isParsing={isParsingStages}
              />
            )}

            {widget === "deal" && (
              <DealStep
                key="deal"
                onSubmit={handleDeal}
                onSkip={handleSkipDeal}
                isSubmitting={busy}
                progress={dealProgress}
                progressLabel={dealProgressLabel}
              />
            )}

            {widget === "invite" && (
              <InviteStep
                key="invite"
                onSubmit={handleInvites}
                onSkip={handleSkipInvites}
                isSubmitting={busy}
              />
            )}

            {widget === "finish" && (
              <FinishStep
                key="finish"
                dealName={finishedDeal?.name}
                onGoToDeal={
                  finishedDeal && workspaceId
                    ? () => leave(`/${workspaceId}/deals/${finishedDeal.id}`)
                    : undefined
                }
                onGoToDashboard={() =>
                  leave(
                    safeReturnPath(searchParams.get("redirect_url")) ??
                      (workspaceId ? `/${workspaceId}/dashboard` : "/workspaces")
                  )
                }
              />
            )}
          </AnimatePresence>
          </div>
        </div>
      </main>

      <motion.footer
        className="shrink-0 pb-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        {state && (
          <p className="text-xs text-muted-foreground/60">
            Your progress saves as you go — you can close this and come back.
          </p>
        )}
      </motion.footer>
      </div>
    </div>
  );
}

/** "Step 2 of 5" — the rail shows where; this says how far. */
function StepCounter({ step }: { step: OnboardingStep }) {
  const index = VISIBLE_STEPS.indexOf(step as (typeof VISIBLE_STEPS)[number]);
  const label =
    index === -1 ? "All set" : `Step ${index + 1} of ${VISIBLE_STEPS.length}`;
  return (
    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{label}</span>
  );
}

/** Guards against a workspace that somehow has no statuses configured. */
function normaliseStages(statuses: PipelineStage[] | undefined): PipelineStage[] {
  if (!statuses || statuses.length === 0) {
    return [
      { title: "New", colour: "#3B82F6", description: "Deal added to pipeline", order: 1 },
      { title: "Reviewing", colour: "#F59E0B", description: "Under initial review", order: 2 },
    ];
  }
  return stripIds(renumber(statuses.map((s) => ({ ...s, id: "" }))));
}
